import {
  Component, Inject, Input, Output, ViewChild, ElementRef, EventEmitter,
  ChangeDetectionStrategy, forwardRef, ChangeDetectorRef,
} from '@angular/core'

import { NG_VALUE_ACCESSOR, ControlValueAccessor, DefaultValueAccessor } from '@angular/forms'

import { IdentityService } from 'main/services/identity.service'
import { NaturalOrderByPipe } from 'common/util/pipes/naturalOrderBy.pipe'
import { APIService } from 'api/module'
import { OpenDialogService } from 'common/util.pickers/services/openDialog.service'

import { from, OperatorFunction } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, mergeMap } from 'rxjs/operators'

import { BootstrapData, StorageRoot } from 'elements-sdk'
import { BOOTSTRAP_DATA } from 'api/providers'

@Component({
  selector: 'path-selector',
  templateUrl: './pathSelector.component.pug',
  styleUrls: [
    './pathSelector.component.scss',
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PathSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PathSelectorComponent implements ControlValueAccessor {
  @Input() relative?: string
  @Input() buckets?: StorageRoot[]
  @Input() placeholder = ''
  @Input() disabled = false
  @Input() readonly = false

  @Output() focus = new EventEmitter<FocusEvent>()
  @Output() blur = new EventEmitter<FocusEvent>()
  @Output() enter = new EventEmitter<void>()

  @ViewChild('input', { read: NG_VALUE_ACCESSOR, 'static': true }) private cva: DefaultValueAccessor
  @ViewChild('input') private input: ElementRef<HTMLInputElement>

  private initialized = false
  private isDisabled = false
  private memo: Record<string, string[]|undefined> = {}
  private S3_RE = /^s3:\/\/([^/]+)?$/

  private viewInitResolver: CallableFunction
  private viewInitPromise = new Promise(resolve => {
    this.viewInitResolver = resolve
  })

  constructor (
    public identity: IdentityService,
    private api: APIService,
    private openDialog: OpenDialogService,
    private changeDetector: ChangeDetectorRef,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
  ) { }

  ngAfterViewInit () {
    this.initialized = true
    this.viewInitResolver()
  }

  async show (): Promise<void> {
    await this.viewInitPromise
    const path = await this.openDialog.show({
      relative: this.relative,
      path: this.input.nativeElement.value,
    }).catch(() => null)
    if (!path) {
      return
    }
    this.writeValue(path)
  }

  async selectBucket (bucket: StorageRoot): Promise<void> {
    await this.viewInitPromise
    this.writeValue(bucket.path)
  }

  search: OperatorFunction<string, readonly string[]> = (text$) => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      mergeMap((term: string) => !term ? [] : from(this.getAutocompletions(term))),
      map((result: string[]) => result.slice(0, 10)),
    )
  }

  async getAutocompletions (query: string): Promise<string[]> {
    let rows: string[] = []
    if (this.buckets && this.S3_RE.exec(query)) {
      query = query.substring(5).toLowerCase()
      let buckets = this.buckets
      if (query) {
        buckets = buckets.filter(x => x.path.toLowerCase().startsWith(query))
      }
      rows = [...rows, ...buckets.map(x => `s3://${x.path}/`)]
    }
    if (query.startsWith(`${this.bootstrapData.settings.DATA_ROOT}/`)) {
      const dir = query.slice(0, -[...query].reverse().indexOf('/'))
      let paths: string[] = []

      if (dir.endsWith('/') && !this.memo[dir]) {
        try {
          const response = await this.api.storage.getFile({ path: dir })
          paths = (response.files ?? []).map(f => f.path)
        } catch {}

        if (paths.length) {
          paths = new NaturalOrderByPipe().transform(paths)
          this.memo[dir] = paths
        }
      }

      for (const [k, v] of Object.entries(this.memo)) {
        if (query.split('/').length === k.split('/').length &&
            query.toLowerCase().startsWith(k.toLowerCase())
        ) {
          rows = [
            ...rows,
            ...(v ?? []).filter(path => {
              return path.toLowerCase().startsWith(query.toLowerCase())
            }),
          ]
        }
      }
    }

    if (rows.length === 1 && rows[0] === this.input.nativeElement.value) {
      return []
    }
    return rows
  }

  // ControlValueAccessor
  writeValue (value: string) {
    this.viewInitPromise = this.viewInitPromise.then(() => {
      this.cva.writeValue(value)
      this.onChange(value)
    })
  }

  onChange = function (_: any) {
    // do nothing
  }

  async registerOnChange (fn) {
    await this.viewInitPromise
    this.onChange = fn
    this.cva.registerOnChange(fn)
  }

  async registerOnTouched (fn) {
    await this.viewInitPromise
    this.cva.registerOnTouched(fn)
  }

  async setDisabledState (isDisabled) {
    await this.viewInitPromise
    this.cva.setDisabledState(isDisabled)
  }
}
